import { Line3, Quaternion, Vector3 } from "three";

import { JointPositions } from "../../types";
import { quaternionBetweenVectors, vectorIsNaN } from "../../misc/util";

import { Joint } from "../joint";

import { clipEdgeLength, Group } from "./group";

const tempP = new Vector3();
const tempA = new Vector3();
const tempB = new Vector3();

const mirrorLine = new Line3();
const tempU = new Vector3();
const tempQ = new Quaternion();

const chainDir = new Vector3();
const targetDir = new Vector3();
const rotQ = new Quaternion();

/**
 * FABRIK inverse kinematics implementation
 */
export class FabrikChain extends Group {
  joints: Joint[];
  chainLen: number;
  effector: Joint;

  annealable = false;

  extent?: [number, number];

  targetPos: Vector3 = new Vector3();

  error = 0;

  minError = 0.08;
  deltaTolerance = 0.05;

  private fixedRoot: boolean;

  /**
   * Follow the tree up to the first root or hub or kabsch joint inserting all intermediate
   * Joints into the chain.
   *
   * @param end       end Joint of chain
   * @param fixedRoot if this is the root chain, we can anchor the root Joint to initial
   *                  position if required
   */
  constructor(end: Joint, fixedRoot: boolean) {
    super();
    this.fixedRoot = fixedRoot;
    // find joints
    const joints = [end];
    let chainLen = 0;
    for (
      let current = end;
      current.parentEdge &&
      (joints.length == 1 ||
        !(current.isHub() || current.isRoot() || current.isKabschChild()));
      current = current.parentEdge.start
    ) {
      chainLen += current.pos.distanceTo(current.parentEdge.start.pos);

      // insert parent at start of chain
      joints.unshift(current.parentEdge.start);
    }

    // label each joint so we can check for membership in solver
    for (const joint of joints) {
      joint.group = this;
    }

    this.joints = joints;
    this.chainLen = chainLen;

    this.effector = end;

    if (end.props.fabrikExtent) {
      this.extent = end.props.fabrikExtent;
    }
  }

  get name(): string {
    return `fabrik: ${this.effector.name}`;
  }

  valid(): boolean {
    return this.joints.length > 1;
  }

  // TODO: replace with accessors
  getEnd(): Joint | null {
    // TODO: should this just return this.effector?
    const numJoints = this.joints.length;
    return numJoints > 0 ? this.joints[numJoints - 1] : null;
  }

  getStart(): Joint | null {
    return this.joints.length > 0 ? this.joints[0] : null;
  }

  /**
   * Solve FABRIK for backward pass (tip to root)
   *
   * @param targets points to target
   * @returns       success
   */
  solveForward(targets: JointPositions): boolean {
    const chainEnd = this.getEnd();

    if (!chainEnd) {
      return false;
    }

    let success = true;
    const { joints } = this;

    if (!this.setEffectorsToTarget(targets)) {
      console.warn("failure to run setEffectorsToTarget");
      return false;
    }

    // FORWARD (tip -> root)
    for (let i = joints.length - 2; i >= 0; i--) {
      const current = joints[i];
      const next = joints[i + 1];

      if (!next.parentEdge) {
        console.error("no parent in forward");
        success = false;
        break;
      }

      if (!clipEdgeLength(next.parentEdge, false)) {
        continue;
      }

      // NB: we currently don't apply constraints forwards across "hubs"
      //     as it leads to very unstable solutions
      if (!current.isHub() && next.parentEdge.hasConstraints()) {
        next.parentEdge.applyConstraints(false);
      }

      // move start pos toward target pos if it exists
      const currentTarget = targets.get(current.name);
      if (targets && next != chainEnd && currentTarget) {
        // TODO: replace weight with metadata
        const weight = current.getWeight();
        tempA.copy(currentTarget).multiplyScalar(weight);
        current.pos.multiplyScalar(1 - weight).add(tempA);
        next.parentEdge.update();
      }

      // set candidate position of hub edge corresponding to this chain
      if (current.isHub()) {
        next.parentEdge.candidateStartPos = current.pos;
      } else if (current.isKabschChild()) {
        targets.set(current.name, current.pos);
      }
    }

    return success;
  }

  /**
   * FABRIK backward pass (root to tip)
   */
  solveBackward(targets: JointPositions): boolean {
    const chainStart = this.getStart();
    const chainEnd = this.getEnd();
    if (!chainStart || !chainEnd) {
      console.error("no start found");
      return false;
    }

    // move root back to init or target position
    if (chainStart.isRoot()) {
      if (this.fixedRoot) {
        chainStart.pos.copy(chainStart.initPos);
      } else {
        const rootTarget = targets.get(chainStart.name);
        if (rootTarget) {
          chainStart.pos.copy(rootTarget);
        } else {
          console.warn(`no root target for ${chainStart.name}`);
        }
      }
    }

    // BACKWARD (root -> tip)
    let success = true;
    const { joints } = this;
    for (let i = 0; i < joints.length - 1; i++) {
      const current = joints[i];
      const next = joints[i + 1];

      if (!next.parentEdge) {
        console.error("no parent in forward");
        success = false;
        break;
      }

      // move end pos toward target pos if it exists
      const nextTarget = targets.get(next.name);
      if (nextTarget) {
        const weight = next.getWeight();
        tempB.copy(nextTarget).multiplyScalar(weight);
        next.pos.multiplyScalar(1 - weight).add(tempB);
        next.parentEdge.update();
      }

      if (!clipEdgeLength(next.parentEdge, true)) {
        continue;
      }

      if (current.parentEdge) {
        if (current.parentEdge.hasConstraints()) {
          // this updates `end` in this scope, as that's the movable point
          // NB: we pass the child edge here so we only apply the constraint
          //     to that child edge - constraints are independent across hubs
          //     for the backward pass
          current.parentEdge.applyConstraints(true, next.parentEdge);
        }
      }
    }

    return success;
  }

  calcError(): [number, number] {
    const start = this.getStart();
    const end = this.getEnd();
    if (!start || !end) {
      return [0, 0];
    }

    const error = this.targetPos.distanceTo(this.effector.pos);

    const delta = this.error - error;

    return [error, delta];
  }

  cacheError() {
    const [error, _] = this.calcError();
    this.error = error;
  }

  diverging(delta: number): boolean {
    return delta < -this.deltaTolerance;
  }

  converged(error: number): boolean {
    return Math.abs(error) < this.minError;
  }

  // TODO: have the base implementation be random and allow for overriding implementations?
  anneal(targets: JointPositions) {
    this.setExtent();
    this.rotateTowardsTarget(targets);
  }

  mirror() {
    const chainStart = this.getStart();

    if (!chainStart) {
      return;
    }

    mirrorLine.set(chainStart.pos, this.targetPos);

    for (let i = 1; i < this.joints.length; i++) {
      const end = this.joints[i];
      const edge = end.parentEdge;

      end.prevPos.copy(end.pos);

      if (edge) {
        edge.qPrev.copy(edge.q);
      }

      mirrorLine.closestPointToPoint(end.pos, true, tempU);

      if (vectorIsNaN(tempU) || tempU.equals(end.pos)) {
        continue;
      }

      tempU.sub(end.pos).multiplyScalar(2).add(end.pos);

      quaternionBetweenVectors(tempQ, end.pos, tempU);

      end.pos.copy(tempU);

      if (edge) {
        edge.q.multiply(tempQ);
        edge.updateQuaternions();
      }
    }
  }

  rotateTowardsTarget(targets?: JointPositions) {
    const chainStart = this.getStart();
    const chainEnd = this.getEnd();
    if (!chainStart || !chainEnd) {
      return;
    }

    const targetEnd = targets?.get(chainEnd.name);
    if (targets && !targetEnd) {
      return;
    }

    // if (chainEnd.name != "Neck")
    //   return;

    chainDir.subVectors(chainEnd.pos, chainStart.pos);
    targetDir.copy(targetEnd ? targetEnd : this.targetPos);
    targetDir.sub(chainStart.pos);

    quaternionBetweenVectors(rotQ, chainDir, targetDir);

    this.rotate(rotQ);
  }

  private solveReach(chain: FabrikChain, target: Vector3): boolean {
    // NB: this function isn't used and hasn't been tested
    // TODO: add constraint enforcement here to make this usable
    let success = true;
    const { joints } = chain;
    for (let i = 0; i < joints.length - 2; i++) {
      const current = joints[i];
      const next = joints[i + 1];

      if (!next.parentEdge) {
        console.error("no parent in reach");
        success = false;
        break;
      }

      const remain = current.pos.distanceTo(target);
      if (remain == 0) {
        console.error("no distance to target in reach");
        return false;
      }

      const length = next.parentEdge.edgeLen;

      const ratio = length / remain;

      tempP.copy(current.pos).multiplyScalar(1 - ratio);
      next.pos.copy(target).multiplyScalar(ratio).add(tempP);

      if (current.parentEdge) {
        if (current.parentEdge.hasConstraints()) {
          // this updates `end` in this scope, as that's the movable point
          // NB: we pass the child edge here so we only apply the constraint
          //     to that child edge - constraints are independent across hubs
          //     for the backward pass
          current.parentEdge.applyConstraints(true, next.parentEdge);
        }
      }
    }

    return success;
  }

  private setEffectorsToTarget(targets: JointPositions): boolean {
    const end = this.getEnd();
    const start = this.getStart();
    if (!end || !start) {
      console.error("no end");
      return false;
    }

    if (this.effector != end) {
      // TODO: remove either chain.end or chain.effector
      console.error("invalid chain end != effector");
      return false;
    }

    // // set end effector to target pos
    // if (end.isHub())
    // {
    //   if (!end.resolveCandidatePositions())
    //   {
    //     console.warn("unable to resolve candidate positions for hub");
    //     return false;
    //   }
    // }
    // else
    // {
    //   if (targets.hasOwnProperty(this.effector.name))
    //     end.pos.copy(targets[this.effector.name]);
    // }

    // set other targeted joint positions
    for (let i = this.joints.length - 1; i >= 0; i--) {
      const joint = this.joints[i];
      const jointTarget = targets.get(joint.name);
      if (joint == end && end.isHub()) {
        if (!end.resolveCandidatePositions()) {
          console.warn(
            "unable to resolve candidate positions for hub, using previous",
            this
          );
          const endTarget = targets.get(end.name);
          if (endTarget) {
            end.pos.copy(endTarget);
          } else {
            end.pos.copy(this.targetPos);
          }
        }
      } else if (jointTarget) {
        joint.pos.copy(jointTarget);
      }

      if (joint != start) {
        joint.parentEdge?.update();
      }
    }

    // cache target position
    this.targetPos.copy(end.pos);

    return true;
  }
}
